import { lookupStore } from './index'
import { getOwner } from '@ember/owner'
import { resolveStoreProp } from './index'

/**
 * Decorator to inject a property from a store.
 *
 * Usage:
 *   - `@context('store') propertyName`
 *       → Injects `store.propertyName` from the specified store.
 *
 *   - `@context('store:storeProperty') localAlias`
 *       → Injects `store.storeProperty` and assigns it to `this.localAlias`.
 *
 *   - `@context('store:*') storeName`
 *       → Injects the entire store as `this.storeName`.
 *
 * Behavior:
 *   - The decorated property reflects the reactive value of the referenced property on the store.
 *   - Assignments to the decorated property will write through to the store and trigger reactivity.
 *
 * Stores can be found at `customer-flows/stores`.
 */
export function context<T extends PrefixedStoreField | StoreName>(modulePath: T): any {
  return function decorator(_target: unknown, propertyKey: StoreField | '*') {
    return {
      configurable: true,
      enumerable: true,
      get(): any {
        let [storeName, storeProperty] = modulePath?.split(':') as [keyof StoreRegistry, StoreField | '*']
        storeProperty = storeProperty || propertyKey

        if (storeProperty === '*') {
          return lookupStore(getOwner(this), storeName)
        }

        return resolveStoreProp(this, storeName, storeProperty as StoreFieldKey<typeof storeName>)
      },
      set(value: any): void {
        let [storeModulePath, propertyName] = modulePath?.split(':') as [keyof StoreRegistry, StoreField]
        propertyName = (propertyName || propertyKey)

        const owner = getOwner(this)
        if (!owner) {
          throw new Error('Unable to find the owner. Ensure you call this function with the correct `this` context.`')
        }

        const store = lookupStore(owner, storeModulePath)

        if (!store) {
          throw new Error(`could not resolve store "${storeModulePath}".`)
        }

        if (!(propertyName in store)) {
          throw new Error(`"${propertyName}" could not be resolved in store "${storeModulePath}".`)
        }

        if (typeof store[(propertyName as StoreFieldKey<typeof storeModulePath>)] === 'function') {
          throw new Error(`"${propertyName}" is a function and cannot be set.`)
        }

        ;(store as Record<string, any>)[propertyName] = value
      }
    }
  }
}
